import get from "lodash/get";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Fade from "@mui/material/Fade";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import makeStyles from "@mui/styles/makeStyles";

import {
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineExpansionDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
  TimelineTransparentConnector,
} from "./StyledTimelineComponents";

import type { GroupedMeasurement, GroupedPanel, WorkflowReducer } from "../../workflowReducer";

import { ValidationMessage } from "../../../shared/ValidationMessage";
import { MeasurementGroupHeader } from "../MeasurementGroupHeader";
import { PanelTimelineItems } from "./PanelTimelineItems";
import { getSubAssetType } from "../../../shared/interfaces/SurveyableAsset";

interface Props {
  workflowReducer: WorkflowReducer;
  measurement: GroupedMeasurement;
  isFirstItemInTimeline?: boolean;
  editMode?: boolean;
  onPanelAddButtonClick: (groupId: string) => void;
}

const useStyles = makeStyles((theme) => ({
  panelActions: {
    padding: theme.spacing(1, 2),
  },
  panelCount: {
    padding: theme.spacing(1),
    fontSize: "1.2em",
    fontWeight: 500,
  },
  dividerContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
  divider: {
    flex: "1 1 auto",
  },
  panelValidation: {
    marginLeft: theme.spacing(2),
  },
}));

export function MeasurementGroupTimelineItem(props: Props) {
  const { t } = useTranslation();
  const classes = useStyles();

  const {
    register,
    unregister,
    formState: { errors },
    setValue,
  } = useFormContext();

  const groupId = props.measurement.groupId;
  const panelCount = props.measurement.panels.length;

  const [panelAccordionExpanded, setPanelAccordionExpanded] = useState(panelCount === 0);

  const { workflowType } = props.workflowReducer.state;

  useEffect(() => {
    props.workflowReducer.state.workflowType === "substation" &&
      register(`measurementsTab.${groupId}.panelCount`, {
        value: panelCount,
        validate: {
          positive: (value) => value > 0,
        },
      });
  }, [groupId, panelCount, props.workflowReducer.state.workflowType, register]);

  useEffect(() => {
    setValue(`measurementsTab.${groupId}.panelCount`, panelCount, {
      shouldValidate: panelCount > 0,
    });
  }, [setValue, panelCount, groupId]);

  function handleMeasurementGroupDetailsEdit(updatedMeasurement: GroupedMeasurement, togglePanelAccordion: boolean) {
    props.workflowReducer.changeMeasurementGroupDetails(updatedMeasurement);
    togglePanelAccordion && setPanelAccordionExpanded(true);
  }

  function togglePanelAccordion() {
    setPanelAccordionExpanded((prevState) => !prevState);
  }

  function handleAddPanelButtonClick() {
    props.onPanelAddButtonClick(props.measurement.groupId);
  }

  function handlePanelDeleteButtonClick(panel: GroupedPanel) {
    props.workflowReducer.removePanelFromGroup(props.measurement.groupId, panel);
  }

  function handleGroupDeleteButtonClick() {
    unregister(`measurementsTab.${groupId}.asset`);
    unregister(`measurementsTab.${groupId}.assetType`);
    unregister(`measurementsTab.${groupId}.measurementComponent`);
    unregister(`measurementsTab.${groupId}.accessLocation`);
    unregister(`measurementsTab.${groupId}.measurementType`);
    unregister(`measurementsTab.${groupId}.panelCount`);

    props.workflowReducer.removeMeasurementGroup(props.measurement.groupId);
    setValue(`measurementsTab.measurementGroupCount`, props.workflowReducer.state.groupedMeasurements.length);
  }

  return (
    <>
      <TimelineItem>
        <TimelineOppositeContent />
        <TimelineSeparator>
          {props.isFirstItemInTimeline ? <TimelineTransparentConnector /> : <TimelineConnector />}
          <TimelineDot variant="outlined"></TimelineDot>
          <TimelineConnector />
        </TimelineSeparator>

        <TimelineContent>
          <MeasurementGroupHeader
            measurement={props.measurement}
            editMode={props.editMode}
            onChange={handleMeasurementGroupDetailsEdit}
            onGroupDeleteButtonClick={handleGroupDeleteButtonClick}
            cableNaming={props.workflowReducer.state.cableNaming}
            isDuplicate={props.workflowReducer.state.duplicateMeasurementGroupIds.includes(props.measurement.groupId)}
            assetId={props.workflowReducer.state.workflow.workflowDetail.switchgear_id}
            workflowType={workflowType}
            workflowReducer={props.workflowReducer}
          />
        </TimelineContent>
      </TimelineItem>

      {workflowType === "substation" && (
        <>
          <TimelineItem>
            <TimelineOppositeContent />
            <TimelineSeparator>
              <TimelineConnector />
              <span onClick={togglePanelAccordion}>
                <TimelineExpansionDot variant="outlined">
                  {panelAccordionExpanded ? (
                    <KeyboardArrowDownIcon fontSize="inherit" color="primary" />
                  ) : (
                    <KeyboardArrowRightIcon fontSize="inherit" color="primary" />
                  )}
                </TimelineExpansionDot>
              </span>
              <TimelineConnector />
            </TimelineSeparator>

            <TimelineContent>
              <Grid container>
                <Grid item xs="auto" component={TimelineContent}>
                  <Typography variant="h6" component="span" className={classes.panelCount}>
                    {panelCount}
                  </Typography>
                  {t("workflows.panelsSelectedCount", {
                    count: panelCount,
                    val: getSubAssetType(props.measurement.parentAssetType),
                  })}
                </Grid>
                {props.editMode && (
                  <Grid item>
                    <Fade in={panelAccordionExpanded}>
                      <Grid container alignItems="center" className={classes.panelActions}>
                        <Divider orientation="vertical" flexItem variant="fullWidth" />
                        <Tooltip
                          title={t<string>("panels.addPanels", {
                            val: getSubAssetType(props.measurement.parentAssetType),
                          })}
                        >
                          <Button color="primary" onClick={handleAddPanelButtonClick}>
                            {t("panels.addPanels", { val: getSubAssetType(props.measurement.parentAssetType) })}
                          </Button>
                        </Tooltip>
                      </Grid>
                    </Fade>
                  </Grid>
                )}
                {props.editMode && get(errors, `measurementsTab.${props.measurement.groupId}.panelCount`) && (
                  <Grid item xs={12} className={classes.panelValidation} component={TimelineContent}>
                    <ValidationMessage
                      invalidMessage={t("workflows.panelsRequired", {
                        val: getSubAssetType(props.measurement.parentAssetType),
                      })}
                      validationType="error"
                      showIcon
                    />
                  </Grid>
                )}
              </Grid>
            </TimelineContent>
          </TimelineItem>
          <Collapse in={panelAccordionExpanded} mountOnEnter unmountOnExit>
            <PanelTimelineItems
              measurement={props.measurement}
              onPanelDeleteButtonClick={handlePanelDeleteButtonClick}
              editMode={props.editMode}
            />
          </Collapse>
        </>
      )}
    </>
  );
}
